import { track } from "../../lib/analytics";

function Badge({
  store,
  href,
  live,
  source,
  fallbackId,
}: {
  store: "apple" | "google";
  href: string;
  live: boolean;
  source: string;
  fallbackId: string;
}) {
  const label = store === "apple" ? "App Store" : "Google Play";
  // The label follows launch status; the link follows whether a real URL is
  // set yet. Presented as launched, so it always reads "Download on the …".
  const top = live ? "Download on the" : "Coming soon to";
  const hasHref = Boolean(href);
  const handleClick = (e: React.MouseEvent) => {
    track("download_click", { store, source, live });
    if (!hasHref) {
      e.preventDefault();
      document
        .getElementById(live ? "top" : fallbackId)
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <a
      href={hasHref ? href : live ? "#top" : `#${fallbackId}`}
      onClick={handleClick}
      className="group inline-flex items-center gap-3 rounded-2xl border-[3px] border-navy bg-navy-950 px-4 py-2.5 shadow-pop-sm transition-all duration-150 hover:translate-x-[3px] hover:translate-y-[3px] hover:bg-navy-800 hover:shadow-none"
      aria-label={`${top} ${label}`}
    >
      <span className="text-white">
        {store === "apple" ? <AppleGlyph /> : <PlayGlyph />}
      </span>
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-wide text-white/60">
          {top}
        </span>
        <span className="block font-display text-base font-semibold text-white">
          {label}
        </span>
      </span>
    </a>
  );
}

export function StoreBadges({
  appStoreUrl,
  googlePlayUrl,
  live,
  source = "hero",
  fallbackId = "waitlist",
}: {
  appStoreUrl: string;
  googlePlayUrl: string;
  live: boolean;
  source?: string;
  fallbackId?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
      <Badge store="apple" href={appStoreUrl} live={live} source={source} fallbackId={fallbackId} />
      <Badge store="google" href={googlePlayUrl} live={live} source={source} fallbackId={fallbackId} />
    </div>
  );
}

function AppleGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16.365 1.43c0 1.14-.417 2.2-1.25 3.02-.99.98-2.18 1.55-3.28 1.46a3.6 3.6 0 0 1 1.3-3.06C13.9 1.98 15.2 1.4 16.36 1.43zM20.5 17.1c-.6 1.38-.9 2-1.66 3.2-1.06 1.68-2.56 3.77-4.42 3.78-1.65.02-2.08-1.08-4.32-1.07-2.24.01-2.7 1.09-4.36 1.07-1.86-.02-3.28-1.9-4.34-3.58C-1.02 16.6-1.3 10.9 1.3 7.9c1-1.2 2.6-1.96 4.1-1.96 1.55 0 2.52 1.08 3.8 1.08 1.24 0 2-1.08 3.8-1.08 1.36 0 2.8.74 3.83 2.02-3.36 1.84-2.82 6.64.67 7.14z" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path d="M3.6 2.1c-.3.2-.5.6-.5 1.1v17.6c0 .5.2.9.5 1.1l9.3-9.9L3.6 2.1z" fill="#00D2C4" />
      <path d="M16.9 8.2 13 12l3.9 3.8 4.3-2.5c.9-.5.9-1.9 0-2.4L16.9 8.2z" fill="#8B5CF6" />
      <path d="M3.6 2.1 12.9 12l4-3.8L5.9.8C5.1.3 4.2.6 3.6 2.1z" fill="#0D9488" />
      <path d="M3.6 21.9 12.9 12l4 3.8L5.9 23.2c-.8.5-1.7.2-2.3-1.3z" fill="#14B8A6" />
    </svg>
  );
}
